import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BASE_URL, GET_PLACE, POST_PATIENT, PUT_PATIENT } from '../utlilities/config';
import { FiX, FiSave, FiMapPin, FiPhone, FiUser, FiPlus, FiClock, FiCalendar } from 'react-icons/fi';
import NewPlace from './AddPlaceModal';
import Select from 'react-select';

// Spinner component to match other modals
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Processing...</p>
    </div>
  </div>
);

// Form field component
const FormField = ({ label, required, error, touched, children, className = "w-full" }) => (
  <div className={className}>
    <div className="flex justify-between mb-1.5">
      <label className="text-sm font-medium text-gray-700 flex items-center">
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    </div>
    {children}
    {touched && error && (
      <p className="mt-1 text-sm text-red-500 animate-fade-in">{error}</p>
    )}
  </div>
);

// Custom Select styles - improved for modern look and null safety
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minWidth: '200px',
    height: '52px',
    backgroundColor: '#f9fafb',
    boxShadow: state && state.isFocused ? '0 0 0 2px #4f46e5' : 'none',
    borderColor: state && state.isFocused ? '#4f46e5' : provided.borderColor,
    borderRadius: '0.5rem',
    paddingLeft: '8px',
    '&:hover': {
      borderColor: '#4f46e5',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state && state.isSelected
      ? 'rgba(79, 70, 229, 0.1)'
      : state && state.isFocused
      ? 'rgba(79, 70, 229, 0.05)'
      : '#ffffff',
    color: state && state.isSelected ? '#4f46e5' : state && state.isFocused ? '#4f46e5' : '#374151',
    fontWeight: state && state.isSelected ? '500' : '400',
    '&:hover': {
      backgroundColor: 'rgba(79, 70, 229, 0.05)',
      color: '#4f46e5',
    },
    padding: '10px 16px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    position: 'absolute',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9CA3AF',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#374151',
  }),
};

// Compact select style for date fields
const compactSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...provided,
    minHeight: '42px',
    height: '42px',
    backgroundColor: '#f9fafb',
    boxShadow: state && state.isFocused ? '0 0 0 2px #4f46e5' : 'none',
    borderColor: state && state.isFocused ? '#4f46e5' : provided.borderColor,
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#4f46e5',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px',
  }),
};

// Helper functions for date calculations
const calculateAgeFromDOB = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (days < 0) {
    months--;
    days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  return { years, months, days };
};

const calculateDOBFromAge = (years, months, days) => {
  const today = new Date();
  const birthDate = new Date(
    today.getFullYear() - years,
    today.getMonth() - months,
    today.getDate() - days
  );
  // Use local timezone instead of UTC to preserve IST date
  const year = birthDate.getFullYear();
  const month = String(birthDate.getMonth() + 1).padStart(2, '0');
  const day = String(birthDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AddPatientModal = ({ setPatientModal, refreshData, mobileNumber, onPatientSaved, selectedPatient }) => {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');
  const [places, setPlaces] = useState([]);
  const [placeModal, setPlaceModal] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  
  const isEditing = Boolean(selectedPatient);

  // References for keyboard navigation
  const numberref = useRef(null);
  const nameref = useRef(null);
  const dobref = useRef(null);
  const placeref = useRef(null);
  const ageref = useRef(null);
  const monthref = useRef(null);
  const daysref = useRef(null);
  const gendref = useRef(null);
  const btnref = useRef(null);

  useEffect(() => {
    getPlaces();
  }, []);

  // Load patient data if editing
  useEffect(() => {
    if (selectedPatient) {
      formik.setValues({
        mobileNumber: selectedPatient.MobileNo || '',
        patientName: selectedPatient.PatientName || '',
        dob: selectedPatient.DOB ? (() => {
          const date = new Date(selectedPatient.DOB);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        })() : '',
        dobDay: selectedPatient.DOB ? new Date(selectedPatient.DOB).getDate() : '',
        dobMonth: selectedPatient.DOB ? new Date(selectedPatient.DOB).getMonth() + 1 : '',
        dobYear: selectedPatient.DOB ? new Date(selectedPatient.DOB).getFullYear() : '',
        patientPlace: selectedPatient.Place || '',
        patientAge: selectedPatient.Age || '',
        patientMonths: selectedPatient.Month || 0,
        patientDays: selectedPatient.Days || 0,
        patientGender: selectedPatient.Gender || '',
      });

      // Set selected place for the dropdown
      const placeOption = { value: selectedPatient.Place, label: selectedPatient.Place };
      setSelectedPlace(placeOption);
    }
  }, [selectedPatient]);

  // Prevent scrolling on number inputs
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  // Focus first field on mount and when modal opens
  useEffect(() => {
    // Small delay to ensure modal is fully rendered
    const timer = setTimeout(() => {
      const isMobileNumeric = mobileNumber && /^\d+$/.test(mobileNumber);
      if (isEditing && selectedPatient) {
        // If editing, focus on the first field (mobile number)
        numberref.current?.focus();
      } else if (isMobileNumeric) {
        // If mobile number is pre-filled, focus on name field
        nameref.current?.focus();
      } else {
        // Otherwise, focus on mobile number field
        numberref.current?.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isEditing, selectedPatient, mobileNumber]);

  // Get places from API
  const getPlaces = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_PLACE}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        setPlaces(response.data.data);
      }
    } catch (error) {
      console.log('Error fetching places');
    }
  };

  // Handle new place selection
  const handleNewPlaceSelected = (newPlace) => {
    const placeOption = { value: newPlace.PlaceName, label: newPlace.PlaceName };
    setSelectedPlace(placeOption);
    formik.setFieldValue('patientPlace', placeOption.value);
    getPlaces();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };
  
  const isNumeric = /^\d+$/.test(mobileNumber);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      mobileNumber: isNumeric ? mobileNumber : '',
      patientName: isNumeric ? '' : mobileNumber,
      dob: '',
      dobDay: '',
      dobMonth: '',
      dobYear: '',
      patientPlace: '',
      patientAge: '',
      patientMonths: 0,
      patientDays: 0,
      patientGender: '',
    },
    validationSchema: Yup.object().shape({
      mobileNumber: Yup.string()
        .matches(/^[6-9]\d{9}$/, 'Mobile number must be exactly 10 digits and start with 9, 8, 7, or 6').required('Mobile Number is required'),
      patientName: Yup.string().required('Patient Name is required'),
      dob: Yup.date().nullable(),
      dobDay: Yup.number().nullable(),
      dobMonth: Yup.number().nullable(),
      dobYear: Yup.number().nullable(),
      patientPlace: Yup.string().nullable(),
      patientAge: Yup.number().nullable(),
      patientMonths: Yup.number().nullable(),
      patientDays: Yup.number().nullable(),
      patientGender: Yup.string().required('Patient Gender is required'),
    })
    .test('dob-or-age-required', 'Either Date of Birth or Age is required', function (values) {
      const { dob, patientAge, dobDay, dobMonth, dobYear } = values;
      const hasDOB = dobDay && dobMonth && dobYear;
      const hasAge = patientAge;
      
      if (!hasDOB && !hasAge) {
        return this.createError({ path: 'dob', message: 'Either Date of Birth or Age is required' });
      }
      return true;
    }),
    
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (!values.dob) {
          const new_dob = calculateDOBFromAge(values.patientAge, values.patientMonths, values.patientDays);
          values.dob = new_dob;
        } else {
          const ageDetails = calculateAgeFromDOB(values.dob);
          values.patientAge = ageDetails.years;
          values.patientMonths = ageDetails.months;
          values.patientDays = ageDetails.days;
        }

        const data = {
          PatientName: values.patientName,
          MobileNo: values.mobileNumber,
          Gender: values.patientGender,
          Age: values.patientAge,
          DOB: values.dob,
          Place: values.patientPlace,
          Months: values.patientMonths,
          Days: values.patientDays,
        };

        if (selectedPatient) {
          const response = await axios.post(`${BASE_URL}${PUT_PATIENT}`, data, {
            headers: {
              'TenantName': 'Abcd',
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
              'PatientID': selectedPatient?.PatientID,
            },
          });
      
          if (response.status === 200) {
            setPatientModal(false);
            refreshData();
          }
        } else {
          const response = await axios.post(`${BASE_URL}${POST_PATIENT}`, data, {
            headers: {
              'TenantName': 'Abcd',
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
            },
          });
  
          if (response.status === 200) {
            setPatientModal(false);
            refreshData();
  
            const newPatient = response.data.data[0]; 
  
            if (onPatientSaved) {
              onPatientSaved(newPatient);
            }
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
  });

  // Date field options
  const days = Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: i + 1 }));
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1899 }, (_, i) => ({ 
    value: currentYear - i, 
    label: currentYear - i 
  }));

  // Place options for dropdown
  const place_options = places.map(item => ({
    value: item.PlaceName,
    label: item.PlaceName,
  }));

  // Handle DOB change from date input
  const handleDOBChange = (e) => {
    const date = e.target.value;
    formik.setFieldValue('dob', date);
    if (date) {
      const ageDetails = calculateAgeFromDOB(date);
      formik.setFieldValue('patientAge', ageDetails.years);
      formik.setFieldValue('patientMonths', ageDetails.months);
      formik.setFieldValue('patientDays', ageDetails.days);
      
      // Update manual DOB fields
      const dateObj = new Date(date);
      formik.setFieldValue('dobDay', dateObj.getDate());
      formik.setFieldValue('dobMonth', dateObj.getMonth() + 1);
      formik.setFieldValue('dobYear', dateObj.getFullYear());
    }
  };

  // Handle age field changes to update DOB
  const handleAgeChange = (field, value) => {
    formik.setFieldValue(field, value || 0);
    
    const newValues = {
      ...formik.values,
      [field]: value || 0,
    };
    
    const { patientAge, patientMonths, patientDays } = newValues;
    
    // Only calculate DOB if at least years are provided
    if (patientAge !== undefined && patientAge !== '' && patientAge !== null) {
      const calculatedDOB = calculateDOBFromAge(
        parseInt(patientAge) || 0,
        parseInt(patientMonths) || 0,
        parseInt(patientDays) || 0
      );
      formik.setFieldValue('dob', calculatedDOB);
      
      // Update manual DOB fields
      const dateObj = new Date(calculatedDOB);
      formik.setFieldValue('dobDay', dateObj.getDate());
      formik.setFieldValue('dobMonth', dateObj.getMonth() + 1);
      formik.setFieldValue('dobYear', dateObj.getFullYear());
    }
  };

  // Handle manual DOB field changes
  const handleManualDOBChange = (field, value) => {
    formik.setFieldValue(field, value);
  
    const newValues = {
      ...formik.values,
      [field]: value,
    };
    
    const { dobDay, dobMonth, dobYear } = newValues;
  
    if (dobDay && dobMonth && dobYear) {
      const date = new Date(dobYear, dobMonth - 1, dobDay);
      if (!isNaN(date.getTime())) {
        // Use local timezone instead of UTC to preserve IST date
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        formik.setFieldValue('dob', formattedDate);
  
        const ageDetails = calculateAgeFromDOB(formattedDate);
        formik.setFieldValue('patientAge', ageDetails.years);
        formik.setFieldValue('patientMonths', ageDetails.months);
        formik.setFieldValue('patientDays', ageDetails.days);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {loading && <Spinner />}
      
      <div className="bg-white w-4/5 md:w-4/5 lg:w-3/4 rounded-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-zoom-in overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiUser className="mr-2" /> {isEditing ? 'Edit Patient' : 'Add New Patient'}
          </h2>
          <button 
            onClick={() => setPatientModal(false)}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <form onSubmit={formik.handleSubmit} className="p-6 space-y-5">
          {/* Row 1: Mobile Number and Patient Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField 
              label="Mobile Number" 
              required={true}
              error={formik.errors.mobileNumber}
              touched={formik.touched.mobileNumber}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiPhone />
                </div>
                <input
                  type="text"
                  name="mobileNumber"
                  placeholder="Enter Patient Mobile Number"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.mobileNumber && formik.errors.mobileNumber 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                  value={formik.values.mobileNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  ref={numberref}
                  onKeyDown={(e) => handleKeyDown(e, nameref)}
                  maxLength={10}
                />
              </div>
            </FormField>

            <FormField 
              label="Patient Name" 
              required={true}
              error={formik.errors.patientName}
              touched={formik.touched.patientName}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiUser />
                </div>
                <input
                  type="text"
                  name="patientName"
                  placeholder="Enter Patient Name"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.patientName && formik.errors.patientName 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                  value={formik.values.patientName}
                  onChange={(e) => {
                    const value = e.target.value || '';
                    formik.setFieldValue('patientName', value.toUpperCase());
                  }}
                  onBlur={formik.handleBlur}
                  ref={nameref}
                  onKeyDown={(e) => handleKeyDown(e, dobref)}
                />
              </div>
            </FormField>
          </div>

          {/* Row 2: DOB and Place */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField 
              label="Date of Birth" 
              required={true}
              error={formik.errors.dob}
              touched={formik.touched.dob}
            >
              <div className="space-y-2">
                {/* Date Input Field */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                    <FiCalendar />
                  </div>
                  <input
                    type="date"
                    name="dob"
                    value={formik.values.dob || ''}
                    onChange={handleDOBChange}
                    onBlur={formik.handleBlur}
                    onKeyDown={(e) => handleKeyDown(e, placeref)}
                    className={`w-full pl-10 pr-3 py-3 border ${
                      formik.touched.dob && formik.errors.dob 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                    } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                    ref={dobref}
                  />
                </div>
                {/* Manual DOB Fields */}
                <div className="flex gap-2">
                  <div className="w-1/3 relative">
                    <Select
                      options={days}
                      placeholder="Day"
                      value={days.find(day => day.value === parseInt(formik.values.dobDay))}
                      onChange={(selectedOption) => handleManualDOBChange('dobDay', selectedOption?.value)}
                      styles={compactSelectStyles}
                      isSearchable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div className="w-1/3 relative">
                    <Select
                      options={months}
                      placeholder="Month"
                      value={months.find(month => month.value === parseInt(formik.values.dobMonth))}
                      onChange={(selectedOption) => handleManualDOBChange('dobMonth', selectedOption?.value)}
                      styles={compactSelectStyles}
                      isSearchable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div className="w-1/3 relative">
                    <Select
                      options={years}
                      placeholder="Year"
                      value={years.find(year => year.value === parseInt(formik.values.dobYear))}
                      onChange={(selectedOption) => handleManualDOBChange('dobYear', selectedOption?.value)}
                      styles={compactSelectStyles}
                      isSearchable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>
                {/* Display DOB when set */}
                {formik.values.dob && (
                  <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">
                      DOB: {new Date(formik.values.dob).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>
            </FormField>

            <FormField 
              label="Patient Place" 
              required={true}
              error={formik.errors.patientPlace}
              touched={formik.touched.patientPlace}
            >
              <div className="flex justify-between items-center mb-1.5">
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400 z-10">
                    <FiMapPin />
                  </div>
                  <Select
                    options={place_options}
                    placeholder="Select Place"
                    isSearchable
                    name="patientPlace"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    value={selectedPlace || place_options.find(option => option.value === formik.values.patientPlace)}
                    onChange={(selectedOption) => {
                      setSelectedPlace(selectedOption);
                      formik.setFieldValue('patientPlace', selectedOption ? selectedOption.value : '');
                    }}
                    styles={{
                      ...customSelectStyles,
                      control: (base, state) => ({
                        ...customSelectStyles.control(base, state),
                        paddingLeft: '28px'
                      })
                    }}
                    ref={placeref}
                    onMenuClose={() => ageref.current?.focus()}
                    className="w-full"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setPlaceModal(true)}
                  className="ml-2 flex-shrink-0 text-indigo-600 hover:text-indigo-800 p-2 rounded-lg transition-colors hover:bg-indigo-50"
                >
                  <FiPlus size={20} />
                </button>
              </div>
            </FormField>
          </div>

          {/* Row 3: Age Fields and Gender */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center mb-1.5">
                Age <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-3 gap-4">
                <FormField 
                  label="Years" 
                  required={true}
                  error={formik.errors.patientAge}
                  touched={formik.touched.patientAge}
                  className=""
                >
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiClock />
                    </div>
                    <input
                      type="number"
                      name="patientAge"
                      placeholder="Years"
                      className={`w-full pl-10 pr-3 py-3 border ${
                        formik.touched.patientAge && formik.errors.patientAge 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                      } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.patientAge}
                      onChange={(e) => handleAgeChange('patientAge', e.target.value)}
                      onBlur={formik.handleBlur}
                      onKeyDown={(e) => handleKeyDown(e, monthref)}
                      ref={ageref}
                      min="0"
                      max="150"
                    />
                  </div>
                </FormField>
                
                <FormField 
                  label="Months"
                  error={formik.errors.patientMonths}
                  touched={formik.touched.patientMonths}
                  className=""
                >
                  <input
                    type="number"
                    name="patientMonths"
                    placeholder="Months"
                    className={`w-full pl-3 pr-3 py-3 border ${
                      formik.touched.patientMonths && formik.errors.patientMonths 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                    } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                    value={formik.values.patientMonths}
                    onChange={(e) => handleAgeChange('patientMonths', e.target.value)}
                    onBlur={formik.handleBlur}
                    onKeyDown={(e) => handleKeyDown(e, daysref)}
                    ref={monthref}
                    min="0"
                    max="11"
                  />
                </FormField>
                
                <FormField 
                  label="Days"
                  error={formik.errors.patientDays}
                  touched={formik.touched.patientDays}
                  className=""
                >
                  <input
                    type="number"
                    name="patientDays"
                    placeholder="Days"
                    className={`w-full pl-3 pr-3 py-3 border ${
                      formik.touched.patientDays && formik.errors.patientDays 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                    } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                    value={formik.values.patientDays}
                    onChange={(e) => handleAgeChange('patientDays', e.target.value)}
                    onBlur={formik.handleBlur}
                    onKeyDown={(e) => handleKeyDown(e, gendref)}
                    ref={daysref}
                    min="0"
                    max="31"
                  />
                </FormField>
              </div>
              {/* Display calculated age */}
              {(formik.values.patientAge || formik.values.patientMonths || formik.values.patientDays) && (
                <div className="mt-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-sm text-indigo-700 font-medium">
                    Age: {formik.values.patientAge || 0}Y {formik.values.patientMonths || 0}M {formik.values.patientDays || 0}D
                  </p>
                </div>
              )}
            </div>

            <FormField 
              label="Patient Gender" 
              required={true}
              error={formik.errors.patientGender}
              touched={formik.touched.patientGender}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiUser />
                </div>
                <select
                  name="patientGender"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.patientGender && formik.errors.patientGender 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 appearance-none`}
                  value={formik.values.patientGender}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      btnref.current?.focus();
                    }
                  }}
                  ref={gendref}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </FormField>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors duration-200 flex items-center"
              onClick={() => setPatientModal(false)}
            >
              <FiX className="mr-2" /> Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              ref={btnref}
            >
              <FiSave className="mr-2" /> {isEditing ? 'Update Patient' : 'Save Patient'}
            </button>
          </div>
        </form>

        {placeModal && (
          <NewPlace
            setPlaceModal={setPlaceModal}
            refreshData={getPlaces}
            onPlaceSaved={handleNewPlaceSelected}
          />
        )}
      </div>
    </div>
  );
};

export default AddPatientModal;

